import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, DollarSign, BookOpen, Briefcase, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

export default function Admin() {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [trial, setTrial] = useState(7);
  const [tab, setTab] = useState("dash");

  const load = async () => {
    const [a, u, c] = await Promise.all([
      api.get("/admin/analytics"), api.get("/admin/users"), api.get("/config/public"),
    ]);
    setAnalytics(a.data); setUsers(a.data ? u.data.users : []);
    setPlans(c.data.plans); setTrial(c.data.trial_days);
  };
  useEffect(() => { load().catch(()=>toast.error("Admin only")); }, []);

  const savePlans = async () => {
    await api.post("/admin/plans", { plans });
    toast.success("Plans updated");
  };
  const saveTrial = async () => {
    await api.post("/admin/trial", { trial_days: Number(trial) });
    toast.success("Trial updated");
  };

  const stat = (label, val, Icon) => (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-amber-500" />
      </div>
      <div className="mono font-extrabold text-2xl mt-2">{val}</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">Admin</h1>
        <p className="text-slate-600 dark:text-slate-400">Manage users, pricing and content.</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {[["dash", "Dashboard"], ["users", "Users"], ["plans", "Pricing"], ["settings", "Settings"]].map(([k,l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={()=>setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-amber-500 text-amber-600" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "dash" && analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stat("Total users", analytics.users.total, Users)}
          {stat("Active subs", analytics.users.active, Users)}
          {stat("Trial users", analytics.users.trial, Users)}
          {stat("Revenue", `$${analytics.revenue.toFixed(2)}`, DollarSign)}
          {stat("Topics", analytics.content.topics, BookOpen)}
          {stat("Exams", analytics.content.exams, BookOpen)}
          {stat("Jobs", analytics.content.jobs, Briefcase)}
          {stat("Cancelled", analytics.users.cancelled, Users)}
        </div>
      )}

      {tab === "users" && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-left">
              <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">XP</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="p-3">{u.name}</td><td className="p-3">{u.email}</td>
                  <td className="p-3 capitalize">{u.role}</td><td className="p-3 capitalize">{u.subscription_status}</td>
                  <td className="p-3 mono">{u.xp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "plans" && (
        <div className="space-y-3">
          {plans.map((p, idx) => (
            <div key={p.plan_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input value={p.plan_id} disabled />
              <Input value={p.name} onChange={e=>{ const a=[...plans]; a[idx]={...a[idx], name:e.target.value}; setPlans(a); }} />
              <Input type="number" step="0.01" value={p.price} onChange={e=>{ const a=[...plans]; a[idx]={...a[idx], price:Number(e.target.value)}; setPlans(a); }} />
              <Input value={p.billing_period} onChange={e=>{ const a=[...plans]; a[idx]={...a[idx], billing_period:e.target.value}; setPlans(a); }} />
            </div>
          ))}
          <button data-testid="save-plans-btn" onClick={savePlans} className="btn-primary"><Save className="h-4 w-4" /> Save plans</button>
        </div>
      )}

      {tab === "settings" && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 max-w-md space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Trial duration (days)</label>
            <div className="flex gap-2 mt-1">
              {[0,3,7,14,30].map(d => (
                <button key={d} data-testid={`trial-${d}`} onClick={()=>setTrial(d)} className={`pill ${trial === d ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>{d === 0 ? "No trial" : `${d}d`}</button>
              ))}
            </div>
          </div>
          <button data-testid="save-trial-btn" onClick={saveTrial} className="btn-primary"><Save className="h-4 w-4" /> Save trial</button>
        </div>
      )}
    </div>
  );
}
